import { motion } from "framer-motion";
import {
  EnvelopeIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";

import { useLocation } from "react-router-dom";

export default function EtFooter() {

  // First Created - 15 Aug 2025
  // Author - Atul Pandey
  // Updated - 07 Nov 2025 - Refactored to support variant prop (eternity / trinity)
  // Updated - 20 Nov 2025 - Refactored with new approach - useLocation and aligned to header items 
  // Updated - 25 Nov 2025 - Commented Investors links - Thesis, Growth


  // -------------------------------------------------------------
  // 1. Detect Current Page Variant
  // -------------------------------------------------------------
  const { pathname } = useLocation();
  let variant = "eternity";

  if (pathname.startsWith("/trinity/platform")) {
    variant = "platform";
  } else if (pathname.startsWith("/trinity/meet")) {
    variant = "trinity";
  } else if (pathname.startsWith("/clients")) {
    variant = "clients";
  } else if (pathname.startsWith("/investors")) {
    variant = "investors";
  } else {
    variant = "eternity";
  }

  const year = new Date().getFullYear();

  // -------------------------------------------------------------
  // 2. Explore Column (variant-specific)
  // -------------------------------------------------------------

  const navColsEternity = [
    {
      heading: "Explore",
      links: [
        { label: "Mission", href: "/#mission" },
        { label: "Approach", href: "/#approach" },
        { label: "Voices", href: "/#success" },
        { label: "ThinkWare", href: "/thinkware" },   // ✅ 21 Nov 2025
        { label: "Trinity", href: "/trinity/platform" },
        { label: "Investors", href: "/investors" },
      ],
    },
  ];

  const navColsClients = [
    {
      heading: "Explore",
      links: [
        { label: "CxOs", href: "/clients#clients-hero" },
        { label: "Who We Serve?", href: "/clients#segments" },
        { label: "Where Are You?", href: "/clients#growth-states" },
        { label: "Mind Frames", href: "/clients#inner-states" },
        { label: "ThinkWare", href: "/thinkware" },   // ✅ 21 Nov 2025
        { label: "Voices", href: "/clients#success" },
        { label: "Investors", href: "/investors" },
      ],
    },
  ];

  const navColsInvestors = [
    {
      heading: "Explore",
      links: [
        { label: "Overview", href: "/investors#investors-hero" },
        // { label: "Thesis", href: "/investors#thesis" }, // 25 Nov 2025
        // { label: "Growth", href: "/investors#pe-growth" },// 25 Nov 2025
        { label: "Trinity", href: "/trinity/platform" },
        { label: "Eternity", href: "/" },
      ],
    },
  ];

  const navColsTrinityPlatform = [
    {
      heading: "Explore",
      links: [
        { label: "About", href: "/trinity/platform#overview" },
        { label: "Why?", href: "/trinity/platform#problems" },
        { label: "How It Works", href: "/trinity/platform#how-it-works" },
        { label: "CxOs", href: "/clients" },
        { label: "ThinkWare", href: "/thinkware" },   // ✅ 21 Nov 2025
        { label: "Investors", href: "/investors" },
        { label: "Eternity", href: "/" },
      ],
    },
  ];

  const navColsTrinityImmersive = [
    {
      heading: "Explore",
      links: [
        { label: "Platform", href: "/trinity/platform" },
        { label: "ThinkWare", href: "/thinkware" },   // ✅ 21 Nov 2025
        { label: "CxOs", href: "/clients" },
        { label: "Investors", href: "/investors" },
        { label: "Eternity", href: "/" },
      ],
    },
  ];

  // -------------------------------------------------------------
  // 3. Universal Footer Columns (Company, Legal)
  // -------------------------------------------------------------

  const companyCol = {
    heading: "Company",
    links: [
      { label: "About Us", href: "/about" },
      { label: "Contact", href: "/#contact" },
    ],
  };

  const legalCol = {
    heading: "Legal",
    links: [
      { label: "Privacy", href: "/privacy" },
      { label: "Terms", href: "/terms" },
      { label: "FAQ", href: "/faq" },
    ],
  };

  // -------------------------------------------------------------
  // 4. Select Explore Column Based on Variant
  // -------------------------------------------------------------

  const exploreCol =
    variant === "platform"
      ? navColsTrinityPlatform
      : variant === "trinity"
      ? navColsTrinityImmersive
      : variant === "clients"
      ? navColsClients
      : variant === "investors"
      ? navColsInvestors
      : navColsEternity;

  // Final footer cols: Explore + Company + Legal
  const navCols = [...exploreCol, companyCol, legalCol];

  // -------------------------------------------------------------
  // 5. Brand Settings
  // -------------------------------------------------------------

  const brandText =
    variant === "platform" || variant === "trinity"
      ? "trinity"
      : "eternity";

  const brandHref =
    variant === "trinity"
      ? "/trinity/meet"
      : variant === "platform"
      ? "/trinity/platform"
      : "/";

  const email = "trinity@eternity-solutions.com";

  // -------------------------------------------------------------
  // 6. Back to Top Logic
  // -------------------------------------------------------------

  const topHref =
    variant === "platform"
      ? "/trinity/platform#platform"
      : variant === "trinity"
      ? "/trinity/meet#home"
      : variant === "clients"
      ? "/clients#clients-hero"
      : variant === "investors"
      ? "/investors#investors-hero"
      : "/#home";

  // -------------------------------------------------------------
  // 7. Render
  // -------------------------------------------------------------

  return (
    <footer className="bg-black text-gray-300 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">

          {/* BRAND COLUMN */}
          <div>
            <a
              href={brandHref}
              className="text-2xl font-bold text-orange-500 hover:text-orange-600 transition"
            >
              {brandText}
            </a>

            <p className="mt-4 text-sm text-gray-400 leading-relaxed">
              
                For leaders who want more than growth. For those who seek <span className="text-orange-600">clarity</span> of <span className="text-orange-600">purpose</span>. Strength of <span className="text-orange-600">execution</span>. And a <span className="text-orange-600">rhythm</span> that sustains both.
                
            </p>


            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <EnvelopeIcon className="h-5 w-5 text-orange-500 shrink-0" />
                <a
                  href={`mailto:${email}`}
                  className="hover:text-orange-600 transition"
                >
                  {email}
                </a>
              </div>

              <div className="flex items-start gap-3">
                <MapPinIcon className="h-5 w-5 text-orange-500 shrink-0" />
                <span>Belmont, CA</span>
              </div>
            </div>
          </div>

          {/* FOOTER NAV COLUMNS */}
          {navCols.map((col) => (
            <div key={col.heading}>
              <h4 className="text-white font-semibold">{col.heading}</h4>
              <ul className="mt-4 space-y-3 text-sm">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      className="hover:text-orange-600 transition"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

        </div>

        {/* BOTTOM BAR */}
        <div className="mt-10 border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <p>
            © {year} Eternity Solutions Corp ·{" "}
            {brandText.charAt(0).toUpperCase() + brandText.slice(1)}.  
            All rights reserved.
          </p>

          <div className="flex items-center gap-4">
            <a href="/privacy" className="hover:text-orange-600">
              Privacy
            </a>
            <a href="/terms" className="hover:text-orange-600">
              Terms
            </a>

            <a
              href={topHref}
              className="rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2 text-gray-300"
            >
              Back to Top ↑
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}